import { useDispatch } from 'react-redux';

import { ERROR_TYPES } from '@api/ecommerce-xp';
import {
	useFetch as useFetchCore,
	UseFetchHookOutput,
	UseFetchInput,
	useShowHide,
} from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { EnginesCatalog } from '@bcp-nextgen-dx-touchpoint-framework/react-js';
import { GLOBAL_LOADER } from '@components';
import { BusinessOperationKeys, TBusinessOperationKeys } from '@configs';
import { useLogger } from '@hooks';
import { useNavigation } from '@navigation';
import { saveErrorServiceResponse, usePTCMetadata } from '@store';
import { Anything } from '@types';

export const useFetch = <TInput, TOutput>(
	input: Omit<UseFetchInput<TInput, TOutput>, 'fetchFn' | 'options'> & {
		operationKey: TBusinessOperationKeys;
		withLoading?: boolean;
	}
): UseFetchHookOutput<TInput, TOutput> => {
	const { operationKey, onSuccess, onError, withLoading = true } = input;
	const metadata = usePTCMetadata();
	const logger = useLogger();
	const { navigate } = useNavigation();
	const dispatch = useDispatch();

	const { hide, show } = useShowHide(GLOBAL_LOADER);

	return useFetchCore<TInput, TOutput>({
		fetchFn: (data: TInput) => {
			logger(`useFetch ${operationKey} > request`, { data });

			return new Promise((resolve, reject) => {
				EnginesCatalog.getInstance(EnginesCatalog)
					.getCommunicationEngine()
					.request(
						{
							operationKey: BusinessOperationKeys[operationKey],
						},
						{
							data: { ...data, metadata },
							metadata: {
								messageId: '',
								messageCreatorId: '',
								messageCreationTimestampUTC: new Date(),
								correlationId: '',
								distributedTracingContext: '',
								authenticationValue: '',
								validateOnly: false,
								languageCode: '',
							},
						},
						true,
						undefined,
						true
					)
					.then((jsonData: Anything) => {
						resolve(jsonData);
					})
					.catch((err) => {
						if (Array.isArray(err)) {
							reject({ type: ERROR_TYPES.BUSINESS, errors: err });
						} else if (err instanceof Error) {
							const responseError = {
								type: ERROR_TYPES.SERVICE,
								errors: [{ message: 'We were unable to satisfy your request' }],
							};
							reject(responseError);
						} else {
							reject(err);
						}
					});
			});
		},
		onError: (err: Anything) => {
			if (onError) {
				onError(err);
			} else {
				dispatch(saveErrorServiceResponse(err));
				navigate('serviceError');
			}
		},
		onSuccess,
		options: withLoading
			? {
					loadingFunctions: {
						hide,
						show,
					},
			  }
			: undefined,
	});
};
